import * as angular from 'angular';
import * as _ from 'lodash';

import {
    IdentifierQuery,
    ConnectionPoint,
    HistorianStorageConfiguration,
    ConnectionPointType,
    ComponentDefinition,
    EventTrigger,
    ExportMode,
    IValueViewModel,
    DigitalSetSimpleDictionary,
    LogbookHistorianStorageQueryResult
} from '../../../models';

import {
    IConnectionPointService,
    IHistorianStorageConfigurationService,
    IIdentifierQueryService,
    ILoggerService,
    IStoreService
} from '../../../services';

import { ExportConfigurationViewModel } from './ExportConfigurationViewModel';

import {
    historianStorageConfigurationSaved,
    historianStorageConfigurationSaving,
    toggleEditMode
} from '../../../common/events';
import { IRootScope } from '../../../IRootScope';
import { StoreElement } from '../../../stateManagement';
import { LogbookTableReadMode } from 'widgets/logbookTable/LogbookTableReadMode';

interface DataReadingObject {
    connectionPoints: ConnectionPoint[];
    queries: IdentifierQuery[];
    storageConfigurations: HistorianStorageConfiguration[];
}

class ExportConfigurationController implements angular.IComponentController {
    configurationList: ExportConfigurationViewModel[];
    digitalSets: DigitalSetSimpleDictionary;
    executionResults: { name: string }[];
    hasDuplicates: boolean;
    isEditMode: boolean;
    isExecuting: boolean;
    isLoading: boolean;
    isStoring: boolean;
    loadingMessage: string;
    queries: IdentifierQuery[];
    resultByLogbook: LogbookHistorianStorageQueryResult[];
    selected: ExportConfigurationViewModel;
    selectedGuid: string;
    selectedModel: HistorianStorageConfiguration;
    viewModels: { [guid: string]: IValueViewModel; };

    readonly beginDateTime: string;
    readonly endDateTime: string;
    readonly formController: angular.IFormController;

    private _configurations: HistorianStorageConfiguration[];
    private _connectionPoints: ConnectionPoint[];
    private _unsubscribe: () => void;

    static $inject = [
        '$q',
        '$scope',
        '$rootScope',
        '$state',
        'connectionPointService',
        'historianStorageService',
        'identifierQueryService',
        'translatedLoggerService',
        'storeService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$scope: angular.IScope,
        private _$rootScoope: IRootScope,
        private _$state: angular.ui.IState,
        private _cps: IConnectionPointService,
        private _hcs: IHistorianStorageConfigurationService,
        private _iqs: IIdentifierQueryService,
        private _loggerService: ILoggerService,
        private _storeService: IStoreService,
    ) {
    }

    $onInit() {
        this.configurationList = [];
        if (this._$state.params.historianStorageGuid) {
            this.selectedGuid = this._$state.params.historianStorageGuid;
            this.selected = this.configurationList.find(cl => cl.guid === this.selectedGuid);
        }

        this._unsubscribe = this._storeService.register(store => {
            this.digitalSets = store.digitalSetDictionary.dictionary;
            this.viewModels = store.valueViewModels.dictionary;
        }, [StoreElement.DigitalSets, StoreElement.ValueViewModels]);
        this.refresh();
        this._$scope.$watch(() => this.selected, () => {
            this.hasDuplicates = false;
            if (this.selected) {
                this.selectedModel = _.find(this._configurations, c => c.guid === this.selected.guid);
                if (this.selectedModel?.exportMode === ExportMode.ByIdentifier) {
                    this.execute();
                }
            } else {
                this.selectedModel = null;
            }
        });

        this._$scope.$on(historianStorageConfigurationSaving, () => {
            this.isLoading = true;
            this.loadingMessage = 'Saving configuration';
        });

        this._$scope.$on(historianStorageConfigurationSaved, (event, data: HistorianStorageConfiguration[]) => {
            if (data) {
                this.configurationSaved(data);
            } else {
                this.selected = null;
            }
            this.isLoading = false;
        });

        this.isEditMode = this._$rootScoope.isEditMode;
        this._$scope.$on(toggleEditMode, (event, data) => {
            this.isEditMode = !this.isEditMode;
        });
    }

    $onDestroy(): void {
        this._unsubscribe();
    }

    createNew(obj: { data: { guid: string, type: ConnectionPointType } }) {
        const newConfig: any = {
            trigger: EventTrigger.Scheduled,
            connectionPointGuid: obj.data.guid,
            queryCollection: [],
            name: 'New configuration',
            renamePattern: '{IdentifierName}',
            readMode: 0
        };
        this.createConfig(newConfig);
    }

    duplicate(configuration: ExportConfigurationViewModel) {
        const newConfig: HistorianStorageConfiguration = angular.copy(_.find(this._configurations, c => c.guid === configuration.guid));
        newConfig.guid = null;
        newConfig.name = `${newConfig.name}_1`;
        newConfig.creationDate = null;
        newConfig.creationUser = null;
        newConfig.lastModificationDate = null;
        newConfig.lastModifier = null;
        this.createConfig(newConfig);
    }

    delete(configuration: HistorianStorageConfiguration) {
        this.isLoading = true;
        this.loadingMessage = 'Deleting configuration';
        this._hcs.delete([configuration.guid])
            .then(data => {
                this._configurations = data;
                this.configurationList = this.mapData({
                    connectionPoints: this._connectionPoints,
                    queries: this.queries,
                    storageConfigurations: this._configurations
                });
                this.selected = null;
                this._loggerService.logSuccess(`Successfully deleted configuration ${configuration.name}`);
            })
            .finally(() => this.isLoading = false);
    }

    execute() {
        this.executionResults = [];
        this.isExecuting = true;
        this.hasDuplicates = false;
        this._hcs.execute(this.selectedModel)
            .then(data => {
                const byName = _.countBy(data, d => d.name);
                if (_.some(byName, n => n > 1)) {
                    this.hasDuplicates = true;
                }
                this.executionResults = data;
            })
            .catch(reason => {
                this._loggerService.logError(reason);
            })
            .finally(() => {
                this.isExecuting = false;
            });
    }

    store() {
        if (!this.beginDateTime || !this.endDateTime) {
            this._loggerService.logWarning('Select dates before executing storage');
        } else {
            this.isStoring = true;
            this._hcs.store(this.selectedModel, this.beginDateTime, this.endDateTime)
                .then(data => {
                    if (data.results) {
                        this.resultByLogbook = data.results;
                    }
                    if (data.updatedRowsCount <= 0 || !!data.message) {
                        this._loggerService.logError(data.message);
                    } else {
                        this._loggerService.logSuccess('Successfully stored results.');
                    }
                })
                .finally(() => this.isStoring = false);
        }
    }

    test() {
        if (!this.beginDateTime || !this.endDateTime) {
            this._loggerService.logWarning('Select dates before testing storage');
        } else {
        this.isExecuting = true;
        this.resultByLogbook = [];
        this._hcs.executeByLogbook(this.selectedModel, this.beginDateTime, this.endDateTime)
            .then(data => {
                this.resultByLogbook = data;
            })
            .catch(reason => {
                this._loggerService.logError(reason);
            })
            .finally(() => {
                this.isExecuting = false;
            });
        }
    }

    private configurationSaved(data: any[]) {
        this._configurations = data;
        this.configurationList = this.mapData({ connectionPoints: this._connectionPoints, queries: this.queries, storageConfigurations: data });
        if (this.selected) {
            if (this.selected.guid) {
                this.selected = _.find(this.configurationList, c => c.guid === this.selected.guid);
            } else {
                this.selected = _.find(this.configurationList, c => c.name === this.selected.name);
            }
        } else if (this.selectedModel) {
            this.selected = _.find(this.configurationList, c => c.name === this.selectedModel.name);
        }
        this._loggerService.logSuccess('Successfully saved configuration');
    }

    private refresh() {
        this.isLoading = true;
        this.readData()
            .then((data: DataReadingObject) => {
                this._connectionPoints = data.connectionPoints;
                this.queries = data.queries;
                this._configurations = data.storageConfigurations;
                this.configurationList = this.mapData(data);
                if (this.selectedGuid) {
                    this.selected = this.configurationList.find(cl => cl.guid === this.selectedGuid);
                }
                if (!this.selected && this.configurationList.length > 0) {
                    this.selected = this.configurationList[0];
                }
            })
            .finally(() => this.isLoading = false);
    }

    private readData(): angular.IPromise<DataReadingObject> {
        return this._$q.all([this._cps.getAll(), this._iqs.getAll(), this._hcs.getAll()])
            .then(data => {
                const connectionPoints: ConnectionPoint[] = data[0].map(d => d.connectionPoint);
                const queries: IdentifierQuery[] = data[1].map(dto => dto.query);
                const storageConfigurations: HistorianStorageConfiguration[] = data[2];
                return { connectionPoints, queries, storageConfigurations };
            });
    }

    private mapData(readObject: DataReadingObject): ExportConfigurationViewModel[] {
        try {
            const cpDic = _.keyBy(readObject.connectionPoints, 'guid');
            const queryDic = _.keyBy(readObject.queries, 'guid');
            return readObject.storageConfigurations.map(c => {
                return {
                    name: c.name,
                    guid: c.guid,
                    connectionPointName: cpDic[c.connectionPointGuid] ? cpDic[c.connectionPointGuid].name : '',
                    queries: c.queryCollection.map(q => queryDic[q].name),
                    renamePattern: c.renamePattern
                };
            });
        } catch (e) {
            console.error(e);
        }
    }

    private createConfig(newConfig: HistorianStorageConfiguration) {
        try {
            this._$scope.$broadcast(historianStorageConfigurationSaving);
            this.selectedModel = newConfig;
            this._hcs
                .save([newConfig])
                .then(data => this._$scope.$broadcast(historianStorageConfigurationSaved, data));
        } catch (e) {
            console.error(e);
        }
    }

    private selectConfiguration(configurations: HistorianStorageConfiguration[], guid: string): HistorianStorageConfiguration {
        if (configurations && guid) {
            this.selectedModel = _.find(configurations, c => c.guid === guid);
            this.execute();
        }
        return this.selectedModel;
    }
}

export const exportConfiguration: ComponentDefinition = {
    componentId: 'wtExportConfiguration',
    controller: ExportConfigurationController,
    controllerAs: 'vm',
    template: require('./exportConfiguration.component.html'),
    bindings: {
        beginDateTime: '<',
        endDateTime: '<',
        formController: '<',
        queries: '=?',
        selectedModel: '=selected'
    }
};
